import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "@/app/admin/_common/ui/card";
import { dayjsTz } from "@/app/_utils/dayjs";
import type { FestivalDetailShowRow, FestivalSales } from "@/app/_services/FestivalService";
import { TrendingUp, TrendingDown } from "lucide-react";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const compactCurrencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function CustomTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const point = payload[0].payload;
    return (
      <div className="rounded-md border border-border bg-card px-3 py-2 text-[12px] shadow-sm">
        <div className="font-medium text-foreground">
          {dayjsTz(point.date).format("MMM D, YYYY")}
        </div>
        <div className="text-muted-foreground">
          {currencyFormat.format(point.cumulative_revenue)}
        </div>
      </div>
    );
  }
  return null;
}

function sellThroughPct(show: FestivalDetailShowRow) {
  return show.capacity ? (show.sold / show.capacity) * 100 : 0;
}

function ShowSellThroughRow({
  label,
  show,
  icon: Icon,
  iconClassName,
}: {
  label: string;
  show: FestivalDetailShowRow;
  icon: typeof TrendingUp;
  iconClassName: string;
}) {
  const pct = sellThroughPct(show);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[12.5px]">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <Icon className={`size-4 flex-none stroke-[2.5] ${iconClassName}`} />
          {label}
        </span>
        <span className="font-medium text-foreground">{Math.round(pct)}%</span>
      </div>
      <div className="mb-1.5 truncate text-[13px] text-foreground">{show.title}</div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-muted-foreground/40"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

export default function FestivalSalesCard({
  sales,
  shows,
}: {
  sales: FestivalSales;
  shows: FestivalDetailShowRow[];
}) {
  const chartData = sales.series.map((point) => ({
    ...point,
    timestamp: dayjsTz(point.date).valueOf(),
  }));

  const withCapacity = shows.filter((s) => s.capacity > 0);
  const sorted = [...withCapacity].sort((a, b) => sellThroughPct(b) - sellThroughPct(a));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  const showWorst = sorted.length > 1 && worst.id !== best.id;

  return (
    <Card className="rounded-md p-4 shadow-none">
      <div className="mb-3 text-[13.5px] font-semibold text-foreground">Sales</div>

      <div className="mb-2">
        <div className="mb-1.5 flex items-center justify-between text-[12.5px]">
          <span className="text-muted-foreground">Sell-through</span>
          <span className="font-medium text-foreground">{sales.sell_through_pct}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-foreground"
            style={{ width: `${Math.min(100, sales.sell_through_pct)}%` }}
          />
        </div>
      </div>

      {best && (
        <div className="mt-4 flex flex-col gap-3 border-t border-border pt-3">
          <ShowSellThroughRow
            label="Best seller"
            show={best}
            icon={TrendingUp}
            iconClassName="text-emerald-600"
          />
          {showWorst && (
            <ShowSellThroughRow
              label="Worst seller"
              show={worst}
              icon={TrendingDown}
              iconClassName="text-red-600"
            />
          )}
        </div>
      )}

      <div className="mt-3 h-[150px] pt-2">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="festivalSalesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--foreground))" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(value) => dayjsTz(value).format("MMM YYYY")}
                stroke="hsl(var(--border))"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
              />
              <YAxis
                tickFormatter={(value) => compactCurrencyFormat.format(value)}
                stroke="hsl(var(--border))"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={44}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cumulative_revenue"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.5}
                fill="url(#festivalSalesGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
            No sales yet
          </div>
        )}
      </div>
    </Card>
  );
}
